import React, { useState } from "react";
import { SECTION_TYPES, SECTION_TYPE_LABELS } from "../utils/constants";
import "../styles/SectionEditor.css";

const SectionEditor = ({ section, index, onSave, onCancel }) => {
  const [type, setType] = useState(section[0]);
  const [content, setContent] = useState(section[1]);
  const [error, setError] = useState("");

  const handleSave = () => {
    setError("");

    if (!content.trim()) {
      setError("Content cannot be empty");
      return;
    }

    // Validate special formats
    if (type === SECTION_TYPES.BTN) {
      if (!content.includes("||")) {
        setError('Button format: Label || URL (e.g., "Click Here || https://example.com")');
        return;
      }
    }

    if (type === SECTION_TYPES.YT) {
      if (!content.includes("youtube.com") && !content.includes("youtu.be")) {
        setError("YouTube URL must be a valid YouTube link");
        return;
      }
    }

    if (type === SECTION_TYPES.ALERT) {
      if (!content.includes("||")) {
        setError('Alert format: type || message (e.g., "warning || Alert message")');
        return;
      }
      const [alertType] = content.split("||");
      if (!["warning", "error", "info", "success"].includes(alertType.toLowerCase().trim())) {
        setError('Alert type must be: warning, error, info, or success');
        return;
      }
    }

    if (type === SECTION_TYPES.RESOURCES) {
      // Validate format: Title||URL\nTitle||URL
      const lines = content.split("\n").filter(l => l.trim());
      for (const line of lines) {
        if (!line.includes("||")) {
          setError('Resources format: Title || URL (one per line)');
          return;
        }
      }
    }

    onSave([type, content.trim()], index);
  };

  const renderEditor = () => {
    switch (type) {
      case SECTION_TYPES.TABLE:
        return (
          <div>
            <label>Table (pipe-delimited)</label>
            <textarea
              className="editor-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Header 1 | Header 2 | Header 3&#10;Data 1 | Data 2 | Data 3&#10;Data 4 | Data 5 | Data 6"
              rows={6}
            />
            <div className="hint">Use | to separate columns, each line is a row</div>
          </div>
        );

      case SECTION_TYPES.UL:
        return (
          <div>
            <label>List Items (one per line)</label>
            <textarea
              className="editor-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Item 1&#10;Item 2&#10;Item 3"
              rows={5}
            />
            <div className="hint">Each line becomes a list item</div>
          </div>
        );

      case SECTION_TYPES.BTN:
        return (
          <div>
            <label>Button</label>
            <input
              type="text"
              className="editor-input"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Button Label || https://example.com"
            />
            <div className="hint">Format: Label || URL</div>
          </div>
        );

      case SECTION_TYPES.YT:
        return (
          <div>
            <label>YouTube URL</label>
            <input
              type="url"
              className="editor-input"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
            />
            <div className="hint">Full YouTube URL (youtube.com or youtu.be)</div>
          </div>
        );

      case SECTION_TYPES.RESOURCES:
        return (
          <div>
            <label>Resources (one per line)</label>
            <textarea
              className="editor-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Resource Title || https://example.com&#10;Another Title || https://another.com"
              rows={5}
            />
            <div className="hint">Format: Title || URL (one per line)</div>
          </div>
        );

      case SECTION_TYPES.ALERT:
        return (
          <div>
            <label>Alert</label>
            <div className="alert-type-selector">
              <select
                value={
                  content.includes("||")
                    ? content.split("||")[0].toLowerCase().trim()
                    : "warning"
                }
                onChange={(e) => {
                  const message = content.includes("||")
                    ? content.split("||")[1]
                    : "Message";
                  setContent(`${e.target.value}||${message}`);
                }}
              >
                <option value="warning">⚠️ Warning</option>
                <option value="error">❌ Error</option>
                <option value="info">ℹ️ Info</option>
                <option value="success">✓ Success</option>
              </select>
            </div>
            <input
              type="text"
              className="editor-input"
              value={
                content.includes("||")
                  ? content.split("||")[1]
                  : content
              }
              onChange={(e) => {
                const type = content.includes("||")
                  ? content.split("||")[0]
                  : "warning";
                setContent(`${type}||${e.target.value}`);
              }}
              placeholder="Alert message"
            />
            <div className="hint">Format: type || message</div>
          </div>
        );

      case SECTION_TYPES.MD:
        return (
          <div>
            <label>Markdown</label>
            <textarea
              className="editor-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="# Markdown Heading&#10;&#10;Supports **bold** and *italic*"
              rows={6}
            />
            <div className="hint">Raw markdown text - supports standard markdown syntax</div>
          </div>
        );

      default:
        return (
          <div>
            <label>{SECTION_TYPE_LABELS[type]}</label>
            <textarea
              className="editor-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter text content here..."
              rows={5}
            />
          </div>
        );
    }
  };

  return (
    <div className="section-editor-modal">
      <div className="editor-backdrop" onClick={onCancel} />
      <div className="editor-panel">
        <div className="editor-header">
          <h2>Edit Section #{index + 1}</h2>
          <button className="close-btn" onClick={onCancel}>✕</button>
        </div>

        <div className="editor-body">
          {/* Type Selector */}
          <div className="form-group">
            <label htmlFor="type-select">Section Type</label>
            <select
              id="type-select"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="type-selector"
            >
              {Object.entries(SECTION_TYPES).map(([key, value]) => (
                <option key={value} value={value}>
                  {SECTION_TYPE_LABELS[value]}
                </option>
              ))}
            </select>
            <div className="type-description">
              {getTypeDescription(type)}
            </div>
          </div>

          {/* Content Editor */}
          <div className="form-group">
            {renderEditor()}
          </div>

          {/* Error Message */}
          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="editor-footer">
          <button className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSave}>
            💾 Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const getTypeDescription = (type) => {
  const descriptions = {
    h1: "Main heading - use for page titles",
    h2: "Subheading - use for major sections",
    h3: "Sub-subheading - use for subsections",
    h: "Generic heading - auto-detected",
    p: "Regular paragraph text",
    ul: "Bulleted list - enter items one per line",
    c: "Highlighted callout box - for important notes",
    md: "Raw markdown - supports formatting",
    table: "Data table - use pipes (|) to separate columns",
    btn: "Button link - format: Label || URL",
    yt: "YouTube video - paste the full video URL",
    resources: "External links - format: Title || URL (one per line)",
    alert: "Alert box - choose type and message",
  };
  return descriptions[type] || "";
};

export default SectionEditor;
